// 261010 P9D (#29) — mọi bề mặt prompt = chip `@[Nhãn](kind:id)`. Hàm THUẦN (0 antd). Tái dùng tokenizer `promptChipUtils` (FE) ⇄ `filmPromptRefs.parseRefTokens` (BE) — không viết parser mới.
// Slot `@ImageN` / "Image N" chỉ là VỊ TRÍ lúc gửi engine; thứ LƯU và HIỆN là token bám thực thể.
import { TOKEN_RE, buildToken, type RefChoice, type RefKind } from './promptChipUtils';
import { overrideOf } from './imageStudioUtils';

// ───────────── Sơ đồ định vị ─────────────
export interface MapRefPreview { slot: number; kind: 'PLATE' | 'SHEET' | 'ANCHOR' | string; label: string; url: string }
export interface MapChipCtx {
  locationId: string | null;
  locationName: string;
  cast: Array<{ characterId: string; lookId?: string | null; name: string }>;
  refsPreview: MapRefPreview[];
}

const slotRe = (slot: number): RegExp => new RegExp(`(?<![@\\w])Image ${slot}(?!\\d)`, 'g');

/** Prompt dựng sẵn của sơ đồ ("Image 1 is the location… Image 2 is the character sheet of An…") → chữ có chip: PLATE ⇒ loc:, SHEET ⇒ look: (có lookId) hoặc char:. ANCHOR giữ nguyên chữ (không token — BE đánh số lại lúc gen). */
export function mapPromptToTokens(prompt: string, ctx: MapChipCtx): string {
  let out = String(prompt || '');
  if (!out) return out;
  for (const ref of ctx.refsPreview || []) {
    let token: string | null = null;
    if (ref.kind === 'PLATE' && ctx.locationId) token = buildToken('loc', ctx.locationId, ctx.locationName || 'Bối cảnh');
    else if (ref.kind === 'SHEET') {
      const person = ctx.cast.find((c) => ref.label.includes(`character sheet of ${c.name} (`) || ref.label.includes(`character sheet of ${c.name}:`));
      if (person) token = person.lookId ? buildToken('look', person.lookId, person.name) : buildToken('char', person.characterId, person.name);
    }
    if (token) out = out.replace(slotRe(ref.slot), () => token as string);
  }
  return out;
}

/** Chip chèn được vào prompt sơ đồ: bối cảnh của setup (loc), nhân vật (char), bộ đồ (look). Không có prop/voice/video — sơ đồ không đính các thứ đó. */
export function mapChipChoicesOf(a: {
  locationId: string | null; locationName: string; plateUrl?: string | null;
  characters: Array<{ id: string; name: string }>;
  looks: Array<{ id: string; characterId: string; lookKey: string; canonicalRefUrl?: string | null }>;
}): RefChoice[] {
  const out: RefChoice[] = [];
  if (a.locationId) out.push({ kind: 'loc', id: a.locationId, label: a.locationName || 'Bối cảnh', previewUrl: a.plateUrl ?? null, ready: !!a.plateUrl });
  const nameOf = (id: string) => a.characters.find((c) => c.id === id)?.name || '?';
  const mainSheet = (id: string) => a.looks.find((l) => l.characterId === id && l.canonicalRefUrl)?.canonicalRefUrl ?? null;
  for (const c of a.characters) out.push({ kind: 'char', id: c.id, label: c.name, previewUrl: mainSheet(c.id), ready: !!mainSheet(c.id) });
  for (const l of a.looks) out.push({ kind: 'look', id: l.id, label: `${nameOf(l.characterId)} · ${l.lookKey}`, previewUrl: l.canonicalRefUrl ?? null, ready: !!l.canonicalRefUrl });
  return out;
}

/** Ảnh setup chính (chỉ setup phụ có): chip chỉ đọc cố định cuối danh sách, không token, không gỡ được. */
export function mapAnchorOf(refs: MapRefPreview[] | null | undefined): { url: string; slot: number } | null {
  const a = (refs ?? []).find((r) => r.kind === 'ANCHOR');
  return a ? { url: a.url, slot: a.slot } : null;
}

// ───────────── Storyboard (CHỈ ĐỌC — I1) ─────────────
/** Dòng nhãn ref của storyboard → (kind, nhãn chip). Suy từ chữ BE dựng; không nhận ra ⇒ đạo cụ «Ảnh N» (vẫn thấy ảnh). */
function storyboardRefChip(label: string, slot: number): { kind: RefKind; label: string } {
  const sheet = /character sheet of ([^:(]+?)\s*[:(]/.exec(label);
  if (sheet) return { kind: 'char', label: sheet[1].trim() };
  if (/is the setting seen/.test(label)) return { kind: 'loc', label: 'Hướng nhìn' };
  if (/is the location/.test(label)) return { kind: 'loc', label: 'Bối cảnh' };
  return { kind: 'prop', label: `Ảnh ${slot}` };
}
/** Token hoá `renderedPrompt` + refs của storyboard để HIỆN chip (thumbnail trong câu). Id giả `s<slot>` — chỉ để xem, KHÔNG lưu, KHÔNG gửi đi đâu. */
export function storyboardChipsOf(prompt: string, refs: Array<{ slot: number; label: string; url: string }> | null | undefined): { text: string; choices: RefChoice[] } {
  const list = refs ?? [];
  let text = String(prompt || '');
  const choices: RefChoice[] = [];
  for (const r of list) {
    const c = storyboardRefChip(r.label, r.slot);
    const id = `s${r.slot}`;
    choices.push({ kind: c.kind, id, label: c.label, previewUrl: r.url, ready: true });
    text = text.replace(slotRe(r.slot), () => buildToken(c.kind, id, c.label));
  }
  return { text, choices };
}

// ───────────── Ô Custom Prompt (ảnh Bank) ─────────────
/** `bankImages` của ImageComposer → chip. Khoá `look-<id>` / `loc-<id>` / `prop-<id>` (tab Nhân vật) → đúng kind; khoá trần (tab Bối cảnh / Đạo cụ) → `fallback`. */
export function composerChoicesOf(bank: Array<{ key: string; url: string; label?: string }> | null | undefined, fallback: RefKind): RefChoice[] {
  return (bank ?? []).map((b) => {
    const m = /^(look|loc|prop)-(.+)$/.exec(b.key);
    const kind = (m ? m[1] : fallback) as RefKind;
    return { kind, id: m ? m[2] : b.key, label: (b.label || b.key).replace(/\s*\((người mặc|bối cảnh|đạo cụ)\)\s*$/, ''), previewUrl: b.url, ready: !!b.url };
  });
}

/** Chip → CHỮ (nhãn) trong prompt gửi BE, và ảnh của chip vào `refImages` (BE studio nhận cả hai — 0 thay đổi BE). Ảnh tay giữ chỗ đầu, không nhân đôi. */
export function composerPayloadOf(a: {
  text: string; origText: string;
  refs: Array<{ key: string; url: string; locked?: boolean; isFaceRef?: boolean }>;
  choices: RefChoice[];
}): { promptOverride: string | undefined; refImages: string[] } {
  const byKey = new Map(a.choices.map((c) => [`${c.kind}:${c.id}`, c]));
  const urls: string[] = a.refs.filter((r) => !r.locked && !r.isFaceRef).map((r) => r.url);
  const re = new RegExp(TOKEN_RE.source, 'g');
  const text = String(a.text || '').replace(re, (_raw, label: string, kind: string, id: string) => {
    const c = byKey.get(`${kind}:${id}`);
    if (c?.previewUrl && c.ready && !urls.includes(c.previewUrl)) urls.push(c.previewUrl);
    return String(label).trim();
  });
  return { promptOverride: overrideOf(text, a.origText), refImages: urls };
}

// ───────────── Tạo mặt ─────────────
/** Ô prompt tạo mặt: chip → nhãn chữ (gen từ chữ, không refs); rỗng ⇒ '' (server dùng mặc định). */
export function faceTextOf(text: string): string {
  const re = new RegExp(TOKEN_RE.source, 'g');
  return String(text || '').replace(re, (_r, label: string) => String(label).trim()).trim();
}

// ───────────── Chip sơ đồ không dùng được (vòng sửa 1 #5) ─────────────
/** Mã `overrideIssues` của BE → câu tiếng Việt, kèm gợi ý hành động (gỡ chip / chèn lại chip bối cảnh). Mã lạ ⇒ giữ nguyên chữ, không nuốt. */
export function mapOverrideIssueText(code: string): string {
  const [head, ...rest] = code.split(':');
  const tail = rest.join(':');
  if (head === 'MISSING_LOC_TOKEN') return 'Thiếu chip bối cảnh — sơ đồ cần ảnh bối cảnh: chèn lại chip bối cảnh (gõ @) trước khi gen.';
  if (head === 'TOKEN_UNRESOLVED') return `Chip "${tail}" không còn dùng được (đã xoá, thuộc bối cảnh khác hoặc loại không đính vào sơ đồ) — gỡ chip hoặc chọn lại.`;
  if (head === 'SHEET_MISSING') return `${tail} chưa có ảnh tờ nhân vật — tạo ảnh bộ đồ ở bước 2 hoặc gỡ chip.`;
  if (head === 'REFS_CAPPED') return 'Quá nhiều chip nhân vật (tối đa 7, hoặc 6 khi có ảnh setup chính) — gỡ bớt chip.';
  return code;
}
/** Chip có id nằm trong `overrideIssues` ⇒ `ready:false` để editor tô cam; id lạ không có trong choices thì editor tự tô đỏ. */
export function markChoicesNotReady(choices: RefChoice[], issues: string[] | null | undefined): RefChoice[] {
  const bad = new Set((issues ?? []).filter((c) => c.startsWith('TOKEN_UNRESOLVED:')).map((c) => c.slice('TOKEN_UNRESOLVED:'.length)));
  const badNames = new Set((issues ?? []).filter((c) => c.startsWith('SHEET_MISSING:')).map((c) => c.slice('SHEET_MISSING:'.length)));
  return choices.map((c) => (bad.has(`${c.kind}:${c.id}`) || ((c.kind === 'look' || c.kind === 'char') && badNames.has(c.label.split(' · ')[0])) ? { ...c, ready: false } : c));
}
/** Override CŨ (không token, còn "Image N" thô) ⇒ token hoá như prompt dựng sẵn để người sửa thấy chip; có token ⇒ nguyên văn. */
export function mapEditorValueOf(a: { promptOverride: string | null | undefined; renderedPrompt: string } & MapChipCtx): string {
  const o = a.promptOverride;
  if (o != null && o !== '') return /@\[[^\]\n]*\]\((char|look|loc|prop|voice|video):/.test(o) ? o : mapPromptToTokens(o, a);
  return mapPromptToTokens(a.renderedPrompt, a);
}
